// 196 -- Event Info Pack: the reader + organiser editor for EventDetailBlock
// (082) content, surfaced as the event "Info" tab (authenticated) and, read
// only, on the public tournament page. Self-contained: fetches its own blocks
// via the optional-auth GET, so an anonymous visitor sees only visible blocks
// and no editing controls (the API returns _actions.can_edit=false).
import { useCallback, useEffect, useMemo, useState } from "react";
import type { LucideIcon } from "lucide-react";
import type { EventDetailBlockPublic, EventBlockType, EventVenuePublic } from "@dugads/shared";
import { EVENT_BLOCK_TYPES } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { directionsUrl } from "@/lib/directions";
import {
  MapPin, Clock, Hotel, Bus, Shirt, Dumbbell, Video, Users, Info,
  Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Navigation, Pencil, X,
} from "lucide-react";

// Per-type presentation + a lightweight authoring prefill suggestion. Kept in
// this component (not shared) -- it is UI copy, not domain data.
const BLOCK_META: Record<EventBlockType, { label: string; icon: LucideIcon; suggest: string }> = {
  location: { label: "Location", icon: MapPin, suggest: "Venue & parking" },
  arrival: { label: "Arrival & gate times", icon: Clock, suggest: "Gate & arrival times" },
  accommodation: { label: "Accommodation", icon: Hotel, suggest: "Accommodation" },
  travel: { label: "Travel & buses", icon: Bus, suggest: "Bus schedule" },
  clothing: { label: "Kit & clothing", icon: Shirt, suggest: "Kit & clothing" },
  warmup: { label: "Warm-up", icon: Dumbbell, suggest: "Warm-up" },
  streaming: { label: "Live stream", icon: Video, suggest: "Live stream" },
  roster: { label: "Roster", icon: Users, suggest: "Roster" },
  custom: { label: "Info", icon: Info, suggest: "Info" },
};

// Which block types carry a directions affordance to the event's primary venue.
const LOCATION_TYPES: ReadonlySet<EventBlockType> = new Set(["location", "arrival"]);

interface EventInfoPanelProps {
  eventId: string;
  /** Event venues (for the directions affordance on location/arrival blocks). */
  venues?: EventVenuePublic[];
  /** Public (tournament) rendering: read-only, and render nothing when there
   *  are no visible blocks -- a public page should not show empty scaffolding. */
  publicView?: boolean;
}

export function EventInfoPanel({ eventId, venues, publicView = false }: EventInfoPanelProps) {
  const [blocks, setBlocks] = useState<EventDetailBlockPublic[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const primaryVenue = useMemo(
    () => venues?.find((v) => v.is_primary) ?? venues?.[0] ?? null,
    [venues],
  );

  const reload = useCallback(() => {
    api
      .getEventDetailBlocks(eventId)
      .then((res) => {
        setBlocks(res.blocks);
        setCanEdit(res._actions.can_edit);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [eventId]);

  useEffect(() => reload(), [reload]);

  async function move(index: number, dir: -1 | 1) {
    const next = [...blocks];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBlocks(next); // optimistic
    try {
      await api.reorderEventDetailBlocks(eventId, next.map((b) => b.id));
    } catch {
      reload(); // reconcile on failure
    }
  }

  async function remove(blockId: string) {
    if (!confirm("Remove this info block?")) return;
    try {
      await api.deleteEventDetailBlock(eventId, blockId);
      reload();
    } catch {
      reload();
    }
  }

  async function toggleVisible(block: EventDetailBlockPublic) {
    try {
      await api.updateEventDetailBlock(eventId, block.id, { visible: !block.visible });
      reload();
    } catch {
      reload();
    }
  }

  // 108 precedent: on a hard failure (403 for a non-participant) the section
  // self-hides rather than shouting an error onto the page.
  if (failed) return null;
  if (loading) {
    return (
      <div className="h-20 animate-pulse rounded-lg bg-muted" data-region="info" data-state="loading" />
    );
  }

  // Public page with nothing to show -- render nothing.
  if (publicView && blocks.length === 0) return null;

  const showEditing = canEdit && !publicView;

  return (
    <div className="flex flex-col gap-3" data-region="info">
      {blocks.length === 0 && !showEditing && (
        <p className="text-sm text-muted-foreground">No event information has been added yet.</p>
      )}

      {blocks.map((block, i) => (
        <div key={block.id}>
          {editingId === block.id && showEditing ? (
            <BlockForm
              eventId={eventId}
              block={block}
              onDone={() => { setEditingId(null); reload(); }}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <BlockCard
              block={block}
              primaryVenue={primaryVenue}
              canEdit={showEditing}
              isFirst={i === 0}
              isLast={i === blocks.length - 1}
              onEdit={() => setEditingId(block.id)}
              onDelete={() => remove(block.id)}
              onToggleVisible={() => toggleVisible(block)}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          )}
        </div>
      ))}

      {showEditing && (
        adding ? (
          <BlockForm
            eventId={eventId}
            onDone={() => { setAdding(false); reload(); }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <Button variant="outline" size="sm" className="w-fit" data-action="add-info-block" onClick={() => setAdding(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden /> Add info block
          </Button>
        )
      )}
    </div>
  );
}

function BlockCard({
  block, primaryVenue, canEdit, isFirst, isLast,
  onEdit, onDelete, onToggleVisible, onMoveUp, onMoveDown,
}: {
  block: EventDetailBlockPublic;
  primaryVenue: EventVenuePublic | null;
  canEdit: boolean;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisible: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const meta = BLOCK_META[block.block_type] ?? BLOCK_META.custom;
  const Icon = meta.icon;
  const showDirections = LOCATION_TYPES.has(block.block_type) && primaryVenue != null;
  return (
    <Card data-block={block.id} data-block-type={block.block_type} className={block.visible ? "" : "opacity-60"}>
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-primary" aria-hidden />
          {block.title}
          {!block.visible && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">Hidden</span>
          )}
        </CardTitle>
        {canEdit && (
          <div className="flex shrink-0 items-center gap-0.5">
            <Button variant="ghost" size="sm" aria-label="Move up" disabled={isFirst} onClick={onMoveUp} data-action="move-up">
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" aria-label="Move down" disabled={isLast} onClick={onMoveDown} data-action="move-down">
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" aria-label={block.visible ? "Hide block" : "Show block"} onClick={onToggleVisible} data-action="toggle-visible">
              {block.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 text-muted-foreground" />}
            </Button>
            <Button variant="ghost" size="sm" aria-label="Edit block" onClick={onEdit} data-action="edit-block">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" aria-label="Delete block" onClick={onDelete} data-action="delete-block">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {block.body ? (
          <p className="whitespace-pre-wrap text-sm text-foreground">{block.body}</p>
        ) : (
          <p className="text-sm text-muted-foreground">--</p>
        )}
        {Object.keys(block.meta).length > 0 && (
          <dl className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(block.meta).map(([k, v]) => (
              <div key={k}>
                <dt className="text-muted-foreground">{k}</dt>
                <dd>{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
        {showDirections && (
          <a
            href={directionsUrl(primaryVenue!)}
            target="_blank"
            rel="noopener noreferrer"
            data-action="block-directions"
            className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <Navigation className="h-3.5 w-3.5" aria-hidden />
            Directions to {primaryVenue!.display_name}
          </a>
        )}
      </CardContent>
    </Card>
  );
}

// Create (no block) or edit (block given) form. Reuses the 082 CRUD endpoints.
function BlockForm({
  eventId, block, onDone, onCancel,
}: {
  eventId: string;
  block?: EventDetailBlockPublic;
  onDone: () => void;
  onCancel: () => void;
}) {
  const isEdit = block != null;
  const [type, setType] = useState<EventBlockType>(block?.block_type ?? "arrival");
  const [title, setTitle] = useState(block?.title ?? "");
  const [body, setBody] = useState(block?.body ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Track whether the user has hand-edited the title, so switching type can
  // refresh the prefill suggestion without clobbering their own text.
  const [titleTouched, setTitleTouched] = useState(isEdit);

  function pickType(next: EventBlockType) {
    setType(next);
    if (!titleTouched) setTitle(BLOCK_META[next].suggest);
  }

  async function save() {
    const finalTitle = title.trim() || BLOCK_META[type].suggest;
    setSaving(true);
    setErr(null);
    try {
      if (isEdit) {
        await api.updateEventDetailBlock(eventId, block!.id, {
          block_type: type,
          title: finalTitle,
          body: body.trim() || null,
        });
      } else {
        await api.createEventDetailBlock(eventId, {
          block_type: type,
          title: finalTitle,
          body: body.trim() || null,
          meta: {},
        });
      }
      onDone();
    } catch (e) {
      setErr(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card data-region={isEdit ? "edit-info-block" : "new-info-block"}>
      <CardContent className="space-y-3 pt-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="info-block-type" className="text-xs font-medium">Type</label>
          <select
            id="info-block-type"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={type}
            onChange={(e) => pickType(e.target.value as EventBlockType)}
            data-field="block_type"
          >
            {EVENT_BLOCK_TYPES.map((t) => (
              <option key={t} value={t}>{BLOCK_META[t].label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="info-block-title" className="text-xs font-medium">Title</label>
          <input
            id="info-block-title"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder={BLOCK_META[type].suggest}
            value={title}
            onChange={(e) => { setTitle(e.target.value); setTitleTouched(true); }}
            data-field="title"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="info-block-body" className="text-xs font-medium">Details</label>
          <textarea
            id="info-block-body"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            rows={4}
            placeholder="e.g. Gates open 07:30. Parking on the north field."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-field="body"
          />
        </div>
        {err && <p className="text-sm text-destructive" data-error>{err}</p>}
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-action="save-info-block">
            {isEdit ? "Save" : "Add block"}
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>
            <X className="mr-1 h-4 w-4" /> Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
